import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  IconButton,
  Avatar,
  Slide,
  AppBar,
  Toolbar,
  DialogTitle,
  CircularProgress,
  Tab,
  Tabs,
  Skeleton,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from "react-router-dom";
import { Crown, LogOut, User, Building, CreditCard } from 'lucide-react';
import dayjs from 'dayjs';
import { Notyf } from 'notyf';

// --- Notifications ---
const notyf = new Notyf({
  position: { x: 'left', y: 'bottom' },
  duration: 3000,
});
const notyf2 = new Notyf({
  position: { x: 'center', y: 'top' },
  duration: 3000,
});

// --- Dialog Transition ---
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// --- Detail Display Components ---
const DetailItem = ({ label, value, loading }) => (
  <Box sx={{ display: 'flex', mb: 1.5 }}>
    <Typography variant="body2" sx={{ fontWeight: 'bold', width: '120px', color: 'text.secondary' }}>
      {label}
    </Typography>
    {loading ? <Skeleton variant="text" width={150} /> :
      <Typography variant="body2" color="text.primary">
        {value || '-'}
      </Typography>
    }
  </Box>
);

const UserDetails = ({ user, loading }) => (
  <Box>
    <Typography variant="h6" gutterBottom>User Information</Typography>
    <Divider sx={{ mb: 2 }} />
    <DetailItem label="Username" value={user.name} loading={loading} />
    <DetailItem label="Email" value={user.email} loading={loading} />
  </Box>
);

const CompanyDetails = ({ user, loading, onEdit }) => (
  <Box>
    <Typography variant="h6" gutterBottom>Company Information</Typography>
    <Divider sx={{ mb: 2 }} />
    <DetailItem label="Name" value={user.companyname} loading={loading} />
    <DetailItem label="Email" value={user.companyemail} loading={loading} />
    <DetailItem label="Phone" value={user.phone} loading={loading} />
    <DetailItem label="Address" value={user.address} loading={loading} />
    <DetailItem label="Website" value={user.website} loading={loading} />
    <Button
      variant="contained"
      onClick={onEdit}
      sx={{
        mt: 2,
        color: "#fff",
        textTransform: "none",
        fontWeight: 'bold'
      }}
    >
      Edit Profile
    </Button>
  </Box>
);

const SubscriptionDetails = ({ subscriptions, loading, onUpgrade }) => (
  <Box>
    <Typography variant="h6" gutterBottom>Subscription Details</Typography>
    <Divider sx={{ mb: 2 }} />
    {loading ? (
      <Skeleton variant="rectangular" width="100%" height={100} />
    ) : subscriptions.length > 0 ? (
      subscriptions.map((sub, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <DetailItem label="Current Plan" value={sub.plan} />
          <DetailItem label="Billing Cycle" value={sub.billing_cycle} />
          <DetailItem label="Active Date" value={new Date(sub.start_date).toLocaleDateString()} />
          <DetailItem label="End Date" value={new Date(sub.end_date).toLocaleDateString()} />
        </Box>
      ))
    ) : (
      <Typography>No active subscriptions found.</Typography>
    )}
    <Button
      onClick={onUpgrade}
      variant="contained"
      startIcon={<Crown size={18} />}
      sx={{
        mt: 2,
        background: "linear-gradient(90deg, rgba(9,9,121,1) 0%, rgba(0,212,255,1) 100%)",
        color: "#fff",
        textTransform: "none",
        fontWeight: 'bold'
      }}
    >
      Upgrade Plan
    </Button>
  </Box>
);

// --- Main Profile Component ---
function UserProfile() {
  const [user, setUser] = useState({});
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [companyname, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [companyemail, setCompanyEmail] = useState('');
  const [image, setImage] = useState(null);
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [loading, setLoading] = useState(true);
  const [loading2, setLoading2] = useState(false);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const currentToken = localStorage.getItem("token");
      if (!currentToken) {
        navigate("/");
        return;
      }
      setToken(currentToken);
      setLoading(true);

      try {
        const profileResponse = await axios.get("https://admin.bizstor.lk/public/api/profile", {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        const currentUser = profileResponse.data;
        setUser(currentUser);
        setName(currentUser.name);
        setCompanyName(currentUser.companyname);
        setAddress(currentUser.address);
        setPhone(currentUser.phone);
        setCompanyEmail(currentUser.companyemail);
        setEmail(currentUser.email);
        setWebsite(currentUser.website);

        const subsResponse = await axios.get("https://admin.bizstor.lk/public/api/get-subscription", {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        const allSubscriptions = subsResponse.data.subscription || [];
        const userSubscriptions = allSubscriptions.filter(
          (sub) => sub.email === currentUser.email && sub.status === "active"
        );
        setSubscriptions(userSubscriptions);

      } catch (error) {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
        } else {
          console.error("Error fetching data:", error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);


  const handleOpenLogoutDialog = () => setOpenLogoutDialog(true);
  const handleCloseLogoutDialog = () => setOpenLogoutDialog(false);

  const handleLogout = async () => {
    setLoading2(true);
    try {
      await axios.post('https://admin.bizstor.lk/public/api/logout', null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.removeItem('token');
      setToken('');
      notyf2.success('Logout successfully!');
      navigate('/login');
    } catch (error) {
      notyf2.error('Failed to logout!');
    } finally {
      setLoading2(false);
      handleCloseLogoutDialog();
    }
  };

  const handleOpenDialog = () => setOpen(true);
  const handleCloseDialog = () => setOpen(false);

  const handleImageChange = (e) => setImage(e.target.files[0]);

  const handleUpdateProfile = async () => {
    setLoading2(true);
    const formData = new FormData();
    formData.append('name', name);
    formData.append('companyname', companyname);
    formData.append('address', address);
    formData.append('phone', phone);
    formData.append('email', email);
    formData.append('website', website);
    formData.append('companyemail', companyemail);
    if (image) formData.append('image', image);

    try {
      const response = await axios.post('https://admin.bizstor.lk/public/api/profile/update', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setUser(response.data.user);
      notyf.success('Profile updated successfully!');
      handleCloseDialog();
    } catch (error) {
      notyf.error('Failed to update profile!');
    } finally {
      setLoading2(false);
    }
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleUpgrade = () => navigate("/upgrade");



  useEffect(() => {
    if (!subscriptions || !user?.email) {
      return;
    }

    const processedSubscriptions = new Set();

    const checkExpiredSubscriptions = async () => {
      const today = dayjs().format('YYYY-MM-DD');

      const subscriptionsToProcess = subscriptions.filter(sub => {
        const endDate = dayjs(sub.end_date).format('YYYY-MM-DD');
        const subscriptionKey = `${sub.id}-${today}`;

        return today >= endDate &&
          sub.status !== 'expire' &&
          !processedSubscriptions.has(subscriptionKey);
      });

      for (const sub of subscriptionsToProcess) {
        const subscriptionKey = `${sub.id}-${today}`;
        processedSubscriptions.add(subscriptionKey);

        try {
          const response = await axios.post('https://admin.bizstor.lk/public/api/subscription/expire', {
            email: sub.email
          });

          console.log(`Subscription expired for: ${sub.email}`, response.data);


        } catch (err) {
          console.error('Error expiring subscription:', err);
          processedSubscriptions.delete(subscriptionKey);
        }
      }
    };

    checkExpiredSubscriptions();

    const interval = setInterval(checkExpiredSubscriptions, 60000);

    return () => clearInterval(interval);
  }, [subscriptions, user?.email]);





  return (
    <Box
      sx={{ mt: 10 }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: { sm: 'row', xs: 'column' },
        width: { sm: '95%', xs: '90%' },
        maxWidth: 'auto',
        margin: 'auto',
        padding: '10px',
        bgcolor: 'background.paper',
        border: '1px solid #EDF2F7',
        borderRadius: '16px',
        marginTop: '30px',
        position: 'relative',
        '@media (max-width: 600px)': {
          marginTop: '10px',
        },
        gap: 1
      }}>

        <Box sx={{ width: { xs: '100%', md: '30%' } }}>
          <Box elevation={3} sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {loading ? (
              <Skeleton variant="circular" width={120} height={120} />
            ) : (
              <Avatar
                src={`https://admin.bizstor.lk/storage/app/public/images/${user.image}`}
                alt={user.name}
                sx={{
                  width: 120, height: 120, mb: 1,
                  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
                  border: '3px solid #4caf50',
                }}
              />
            )}

            <Divider sx={{ width: '100%', my: 1 }} />
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              orientation="vertical"
              sx={{
                width: '100%',
                '& .MuiTab-root': { justifyContent: 'flex-start', textTransform: 'none' }, mt: 2
              }}
            >
              <Tab icon={<User />} iconPosition="start" label="User" />
              <Tab icon={<Building />} iconPosition="start" label="Company" />
              <Tab icon={<CreditCard />} iconPosition="start" label="Subscription" />
            </Tabs>
            <Divider sx={{ width: '100%', my: 1 }} />
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <IconButton color="error" onClick={handleOpenLogoutDialog} sx={{ bgcolor: 'error.lighter' }}><LogOut /></IconButton>
            </Box>
          </Box>
        </Box>

        <Box sx={{ width: { xs: '100%', md: '70%' } }}>
          <Box elevation={3} sx={{ p: 4, minHeight: '100%' }}>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>
              Welcome Back, <span style={{ fontWeight: "bold" }}>{user.name}!</span>
            </Typography>
            {tabValue === 0 && <UserDetails user={user} loading={loading} />}
            {tabValue === 1 && <CompanyDetails user={user} loading={loading} onEdit={handleOpenDialog} />}
            {tabValue === 2 && <SubscriptionDetails subscriptions={subscriptions} loading={loading} onUpgrade={handleUpgrade} />}
          </Box>
        </Box>

        <Dialog open={openLogoutDialog} onClose={handleCloseLogoutDialog}>
          <DialogTitle>Confirm Logout</DialogTitle>
          <DialogContent>Are you sure you want to log out?</DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLogoutDialog} disabled={loading2}>Cancel</Button>
            <Button onClick={handleLogout} variant="contained" color="error" disabled={loading2}>
              {loading2 ? <CircularProgress size={24} /> : 'Yes, Logout'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={open} onClose={handleCloseDialog} TransitionComponent={Transition} fullWidth maxWidth="sm">
          <AppBar sx={{ position: 'relative', bgcolor: '#FAFAFA', boxShadow: 'none', borderBottom: '1px solid #e0e0e0' }}>
            <Toolbar>

              <Typography sx={{ ml: 2, flex: 1, color: 'black', fontWeight: 'bold' }} variant="h6" component="div">
                Edit Profile
              </Typography>
              <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
                <CloseIcon sx={{ color: 'red' }} />
              </IconButton>
            </Toolbar>
          </AppBar>
          <DialogContent sx={{ pt: 3 }}>
            {/**<TextField margin="dense" label="Name" size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} />*/}
            <TextField margin="dense" label="Company Name" size="small" fullWidth value={companyname} onChange={(e) => setCompanyName(e.target.value)} />
            <TextField margin="dense" label="Address" size="small" fullWidth value={address} onChange={(e) => setAddress(e.target.value)} />
            <TextField margin="dense" label="Phone" size="small" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
            <TextField margin="dense" label="Website" size="small" fullWidth value={website} onChange={(e) => setWebsite(e.target.value)} />
            <TextField margin="dense" label="Company Email" size="small" fullWidth value={companyemail} onChange={(e) => setCompanyEmail(e.target.value)} />
            <Button variant="outlined" component="label" sx={{ mt: 2 }}>Upload New Photo<input type="file" hidden accept="image/*" onChange={handleImageChange} /></Button>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant='contained' onClick={handleUpdateProfile} disabled={loading2}>
              {loading2 ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {loading2 && (
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'white',
            zIndex: 9999
          }}>
            <l-trio
              size="50"
              speed="1.3"
              color="red"
            ></l-trio>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default UserProfile;